import { useState } from "react";
import { BookOpen, Users2, Clock, ChevronDown, ChevronUp, Zap, Calendar } from "lucide-react";

export default function FamilyDevotionalHistory({ history, children, t, language }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedStudy, setExpandedStudy] = useState(null);

  const completedCount = history?.filter(s => s.completed)?.length || 0;

  if (!history || history.length === 0) {
    return (
      <div className="mt-6">
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
          <div className="p-4" style={{ background: "linear-gradient(135deg, #9B5DE5, #118AB2)" }}>
            <div className="flex items-center gap-2">
              <Clock size={20} color="white" strokeWidth={2.5} />
              <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('family_devotional_history')}
              </h3>
            </div>
          </div>
          <div className="p-8 text-center">
            <Users2 size={28} className="text-[#9B5DE5] mx-auto mb-2 opacity-50" />
            <p className="font-bold text-gray-500">{t('no_family_studies')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('start_first_study')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="bg-white rounded-3xl shadow-lg overflow-hidden border-b-4" style={{ borderColor: "#9B5DE5" }}>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #9B5DE5, #118AB2)" }}
          data-testid="devotional-history-toggle"
        >
          <div className="text-left">
            <div className="flex items-center gap-2">
              <Clock size={20} color="white" strokeWidth={2.5} />
              <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
                {t('family_devotional_history')}
              </h3>
            </div>
            <p className="text-white/80 text-xs mt-1">
              {completedCount} {t('family_studies_completed')} &middot; {history.length} {t('weeks') || 'weeks'}
            </p>
          </div>
          <div className="bg-white/20 rounded-full p-2">
            {expanded ? <ChevronUp size={20} color="white" /> : <ChevronDown size={20} color="white" />}
          </div>
        </button>

        {expanded && (
          <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
            {history.map((study, index) => {
              const isStudyExpanded = expandedStudy === (study.id || index);
              const isUpcoming = study.upcoming;
              const studyDate = new Date(study.date || study.week_start);
              const childrenInStudy = study.children_lessons || [];
              const lessonDayName = study.family_lesson_day_name || "Saturday";
              
              return (
                <div 
                  key={study.id || index}
                  className="rounded-2xl overflow-hidden border-2 transition-all"
                  style={{ 
                    borderColor: isUpcoming ? "#FFD166" : study.completed ? "#06D6A0" : "#E5E7EB",
                    background: isUpcoming ? "#FFFBEB" : study.completed ? "#E0FFF5" : "#F9FAFB"
                  }}
                >
                  <button
                    onClick={() => setExpandedStudy(isStudyExpanded ? null : (study.id || index))}
                    className="w-full p-3 flex items-center gap-3"
                    data-testid={`study-item-${index}`}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: isUpcoming 
                        ? "linear-gradient(135deg, #FFD166, #F77F00)" 
                        : study.completed 
                          ? "linear-gradient(135deg, #06D6A0, #118AB2)" 
                          : "linear-gradient(135deg, #E5E7EB, #D1D5DB)" 
                      }}>
                      <BookOpen size={20} color="white" strokeWidth={2.5} />
                    </div>
                    
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-black text-sm text-[#073B4C] truncate" style={{ fontFamily: "Fredoka, sans-serif" }}>
                        {study.theme || study.title || "Family Bible Study"}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        {/* Lesson Day Badge */}
                        <span className="text-[10px] font-black text-[#9B5DE5] bg-[#9B5DE5]/10 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <Calendar size={9} /> {lessonDayName}
                        </span>
                        {/* Date */}
                        <span className="text-[10px] text-gray-400 font-medium">
                          {studyDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
                            month: 'short', day: 'numeric'
                          })}
                        </span>
                        {/* Status */}
                        {isUpcoming && (
                          <span className="text-[10px] font-black text-[#F77F00] bg-[#FFD166]/20 px-1.5 py-0.5 rounded-full">
                            Upcoming
                          </span>
                        )}
                        {study.completed && (
                          <span className="text-[10px] font-bold text-[#06D6A0] bg-[#06D6A0]/10 px-1.5 py-0.5 rounded-full">
                            {t('completed') || 'Completed'}
                          </span>
                        )}
                        {study.family_streak > 1 && (
                          <span className="text-[10px] font-bold text-[#F77F00] flex items-center gap-0.5">
                            <Zap size={9} /> {study.family_streak}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-gray-400 flex-shrink-0">
                      {isStudyExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </button>

                  {isStudyExpanded && !isUpcoming && (
                    <div className="px-3 pb-3 space-y-2 border-t border-gray-200/50">
                      {study.opening_verse && (
                        <div className="mt-2 rounded-xl p-3" style={{ background: "linear-gradient(135deg, #073B4C, #118AB2)" }}>
                          <p className="text-white/90 text-sm italic font-medium leading-relaxed">
                            "{study.opening_verse}"
                          </p>
                          <p className="text-white/60 text-xs mt-1 font-bold">{study.verse_reference}</p>
                        </div>
                      )}

                      {study.overview && (
                        <div className="rounded-xl p-2.5 bg-white border border-gray-200">
                          <p className="text-[10px] font-bold text-[#9B5DE5] uppercase tracking-wide mb-1">Overview</p>
                          <p className="text-xs text-gray-600 leading-relaxed">{study.overview}</p>
                        </div>
                      )}

                      {childrenInStudy.length > 0 && (
                        <div className="rounded-xl p-2.5 bg-[#E0F4FF] border border-[#118AB2]/20">
                          <p className="text-[10px] font-bold text-[#118AB2] uppercase tracking-wide mb-1.5">Children's Lessons</p>
                          <div className="space-y-1.5">
                            {childrenInStudy.map((cl, i) => (
                              <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-black"
                                  style={{ background: ["#118AB2","#F77F00","#06D6A0","#EF476F","#9B5DE5"][i%5] }}>
                                  {cl.child_name?.[0] || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold text-[#073B4C] truncate">{cl.child_name}</p>
                                  <p className="text-[10px] text-gray-500 truncate">{cl.lesson_title}</p>
                                </div>
                                {cl.quiz_summary && (
                                  <span className="text-[10px] font-bold text-[#06D6A0]">
                                    {cl.quiz_summary.correct_count}/{cl.quiz_summary.total_questions}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {study.notes && (
                        <div className="rounded-xl p-2.5 bg-white border border-gray-200">
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Notes</p>
                          <p className="text-xs text-gray-600">{study.notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {isStudyExpanded && isUpcoming && (
                    <div className="px-3 pb-3 border-t border-yellow-200/50">
                      <div className="mt-2 rounded-xl p-3 bg-yellow-50 border border-yellow-200 text-center">
                        <p className="text-sm font-bold text-[#F77F00]">
                          Family lesson on {lessonDayName}!
                        </p>
                        {study.scripture && (
                          <p className="text-xs text-gray-500 italic mt-1">{study.scripture}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
